// Pages/InventairesPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useProductStore } from "./../../store/productStore";
import {
  BiPlus,
  BiEdit,
  BiTrash,
  BiShow,
  BiHistory,
  BiTrendingUp,
  BiTrendingDown,
  BiAdjust,
  BiX,
  BiCopy,
  BiPackage,
  BiShoppingBag,
  BiCheckCircle,
  BiXCircle,
  BiCalendar,
  BiDownload,
  BiSearch,
  BiFilter,
  BiChevronLeft,
  BiChevronRight,
  BiRefresh,
} from "react-icons/bi";
import {
  FiAlertTriangle,
  FiX,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiDownload,
} from "react-icons/fi";

export default function InventairesPage() {
  const {
    products,
    loading,
    error,
    fetchProducts,
    createMovement,
    fetchInventoryReport,
    inventoryReport,
    historyReport,
    exportInventoryPDF,
    fetchProductHistory,
  } = useProductStore();

  const [modalLoading, setModalLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // États pour la recherche et filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");

  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  // Chargement initial
  useEffect(() => {
    loadProducts();
    loadInventoryReport();
  }, []);

  const loadProducts = async () => {
    try {
      await fetchProducts();
    } catch (error) {
      console.error("Erreur lors du chargement des produits:", error);
      toast.error("Erreur lors du chargement des produits");
    }
  };

  const loadInventoryReport = async () => {
    try {
      await fetchInventoryReport();
    } catch (error) {
      console.error("Erreur lors du chargement du rapport:", error);
      toast.error("Erreur lors du chargement du rapport");
    }
  };

  // Fonction pour exporter le PDF
  const handleExportPDF = async () => {
    try {
      await exportInventoryPDF();
    } catch (error) {
      toast.error("Erreur lors de l'export du PDF");
    }
  };

  // Ouvrir modal d'historique
  const handleShowHistory = async (product) => {
    setModalLoading(true);
    try {
      await fetchProductHistory(product.id);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setModalLoading(false);
    }
  };

  // Obtenir le statut du stock
  const getStockStatus = (product) => {
    const quantity = product.quantity || product.stockActuel || 0;
    const seuilAlerte = product.seuilAlerte || 0;

    if (quantity === 0) return "out-of-stock";
    if (quantity <= seuilAlerte) return "low-stock";
    return "in-stock";
  };

  const getMovementTypeIcon = (type) => {
    switch (type) {
      case "ENTREE":
        return <BiTrendingUp className="text-green-600" size={14} />;
      case "SORTIE":
        return <BiTrendingDown className="text-red-600" size={14} />;
      case "AJUSTEMENT":
        return <BiAdjust className="text-blue-600" size={14} />;
      case "PERDU":
        return <BiXCircle className="text-orange-600" size={14} />;
      default:
        return <BiPackage className="text-gray-600" size={14} />;
    }
  };

  const getMovementTypeLabel = (type) => {
    switch (type) {
      case "ENTREE":
        return "Entrée";
      case "SORTIE":
        return "Sortie";
      case "AJUSTEMENT":
        return "Ajustement";
      case "PERDU":
        return "Perte";
      default:
        return type;
    }
  };

  const getStockStatusColor = (product) => {
    const status = getStockStatus(product);
    switch (status) {
      case "out-of-stock":
        return "bg-red-50 text-red-700 border-red-200";
      case "low-stock":
        return "bg-yellow-50 text-yellow-700 border-yellow-200";
      default:
        return "bg-green-50 text-green-700 border-green-200";
    }
  };

  const getStockStatusLabel = (product) => {
    const status = getStockStatus(product);
    switch (status) {
      case "out-of-stock":
        return "Rupture";
      case "low-stock":
        return "Faible";
      default:
        return "Normal";
    }
  };

  // Fonction pour réinitialiser tous les filtres
  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setStockFilter("");
  };

  // Filtrage des données avec useMemo pour optimiser les performances
  const filteredReport = useMemo(() => {
    return inventoryReport.filter((product) => {
      // Filtre par recherche
      const matchesSearch =
        searchTerm === "" ||
        product.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.code?.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtre par statut d'alerte
      const matchesStatus =
        statusFilter === "" || product.statutAlerte === statusFilter;

      // Filtre par niveau de stock
      const matchesStock =
        stockFilter === "" ||
        (stockFilter === "low" && getStockStatus(product) === "low-stock") ||
        (stockFilter === "out" && getStockStatus(product) === "out-of-stock") ||
        (stockFilter === "normal" && getStockStatus(product) === "in-stock");

      return matchesSearch && matchesStatus && matchesStock;
    });
  }, [inventoryReport, searchTerm, statusFilter, stockFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredReport.length / itemsPerPage);
  const paginatedReport = filteredReport.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Statistiques filtrées
  const filteredStats = useMemo(() => {
    const total = filteredReport.length;
    const lowStock = filteredReport.filter(
      (p) => getStockStatus(p) === "low-stock",
    ).length;
    const outOfStock = filteredReport.filter(
      (p) => getStockStatus(p) === "out-of-stock",
    ).length;
    const totalStock = filteredReport.reduce((sum, product) => {
      const quantity = product.quantity || product.stockActuel || 0;
      return sum + quantity;
    }, 0);

    return { total, lowStock, outOfStock, totalStock };
  }, [filteredReport]);

  // Composant Card pour mobile/tablette
  const InventoryCard = ({ product }) => {
    const stockFinal = product.stock_final || product.stockActuel || 0;

    return (
      <div className="bg-white border border-gray-200 hover:shadow-md transition-shadow">
        <div className="p-4 border-b border-gray-100">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 text-primary">
                <BiPackage size={18} />
              </div>
              <div>
                <h3 className="font-poppins font-semibold text-sm text-gray-900">
                  {product.libelle}
                </h3>
                {product.code && (
                  <p className="font-nunito text-xs text-gray-500">
                    Code: {product.code}
                  </p>
                )}
              </div>
            </div>
            <span
              className={`px-2 py-1 text-xs font-semibold border ${getStockStatusColor(product)}`}
            >
              {getStockStatusLabel(product)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-3">
            <div className="space-y-2">
              <div>
                <p className="font-nunito text-xs text-gray-500">
                  Stock initial
                </p>
                <p className="font-poppins font-medium text-sm">
                  {product.quantity_initiale || 0}
                </p>
              </div>
              <div>
                <p className="font-nunito text-xs text-gray-500">
                  Stock actuel
                </p>
                <p className="font-poppins font-bold text-base">
                  {product.stockActuel || 0}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <BiTrendingUp className="text-green-600" size={12} />
                <span className="font-nunito text-xs text-gray-500">
                  Entrées:
                </span>
                <span className="font-poppins font-bold text-sm text-green-600 ml-1">
                  {product.entrees || 0}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <BiTrendingDown className="text-red-600" size={12} />
                <span className="font-nunito text-xs text-gray-500">
                  Sorties:
                </span>
                <span className="font-poppins font-bold text-sm text-red-600 ml-1">
                  {product.sorties || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2 border-t border-gray-100">
            <div>
              <p className="font-nunito text-xs text-gray-500">Stock final</p>
              <p className="font-poppins font-bold text-lg text-gray-900">
                {stockFinal}
              </p>
            </div>
            <button
              onClick={() => handleShowHistory(product)}
              className="flex items-center gap-1 px-3 py-1 text-xs bg-purple-50 text-purple-700 hover:bg-purple-100"
            >
              <BiHistory size={12} />
              Historique
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Colonnes pour le tableau desktop
  const reportColumns = [
    {
      headerName: "Produit",
      width: "25%",
      render: (product) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 text-primary">
            <BiPackage size={18} />
          </div>
          <div>
            <p className="font-poppins font-semibold text-sm text-gray-900">
              {product.libelle}
            </p>
            {product.code && (
              <p className="font-nunito text-xs text-gray-500">
                Code: {product.code}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      headerName: "Stock Initial",
      width: "10%",
      render: (product) => (
        <p className="font-poppins font-medium text-sm text-gray-900">
          {product.quantity_initiale || 0}
        </p>
      ),
    },
    {
      headerName: "Mouvements",
      width: "15%",
      render: (product) => (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <BiTrendingUp className="text-green-600" size={12} />
            <span className="font-nunito text-xs text-gray-500">Entrées:</span>
            <span className="font-poppins font-bold text-sm text-green-600 ml-1">
              {product.entrees || 0}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <BiTrendingDown className="text-red-600" size={12} />
            <span className="font-nunito text-xs text-gray-500">Sorties:</span>
            <span className="font-poppins font-bold text-sm text-red-600 ml-1">
              {product.sorties || 0}
            </span>
          </div>
        </div>
      ),
    },
    {
      headerName: "Stock Actuel",
      width: "10%",
      render: (product) => (
        <p className="font-poppins font-bold text-base text-gray-900">
          {product.stockActuel || 0}
        </p>
      ),
    },
    {
      headerName: "Statut",
      width: "15%",
      render: (product) => (
        <span
          className={`px-2 py-1 text-xs font-semibold border ${getStockStatusColor(product)}`}
        >
          {getStockStatusLabel(product)}
        </span>
      ),
    },
    {
      headerName: "Stock Final",
      width: "10%",
      render: (product) => {
        const stockFinal = product.stock_final || product.stockActuel || 0;
        return (
          <p className="font-poppins font-bold text-lg text-gray-900">
            {stockFinal}
          </p>
        );
      },
    },
    {
      headerName: "Actions",
      width: "15%",
      render: (product) => (
        <button
          onClick={() => handleShowHistory(product)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-purple-50 text-purple-700 hover:bg-purple-100"
        >
          <BiHistory size={12} />
          Historique
        </button>
      ),
    },
  ];

  return (
    <div className="md:p-6">
      {/* En-tête */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-poppins font-semibold text-xl text-gray-900">
              Gestion des Inventaires
            </h1>
            <p className="font-nunito text-sm text-gray-600 mt-1">
              Suivez et gérez les mouvements de stock en temps réel
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadInventoryReport}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white hover:bg-primary/90 font-nunito text-sm font-semibold disabled:opacity-50"
            >
              <BiRefresh size={16} />
              Actualiser
            </button>
            <button
              onClick={handleExportPDF}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white hover:bg-green-700 font-nunito text-sm font-semibold disabled:opacity-50"
            >
              <BiDownload size={16} />
              Exporter PDF
            </button>
          </div>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BiPackage className="text-primary" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Produits filtrés
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {filteredStats.total}
          </p>
          <p className="font-nunito text-xs text-gray-500">
            sur {inventoryReport.length} total
          </p>
        </div>
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BiShoppingBag className="text-purple-600" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Stock total filtré
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {filteredStats.totalStock}
          </p>
        </div>
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <FiAlertTriangle className="text-yellow-600" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Stock faible
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {filteredStats.lowStock}
          </p>
        </div>
        <div className="bg-white p-4 border border-gray-200">
          <div className="flex items-center gap-2 mb-2">
            <BiXCircle className="text-red-600" size={20} />
            <p className="font-poppins font-medium text-sm text-gray-700">
              Rupture de stock
            </p>
          </div>
          <p className="font-poppins font-bold text-xl text-gray-900">
            {filteredStats.outOfStock}
          </p>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white p-4 border border-gray-200 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <FiSearch className="text-gray-400" size={18} />
            </div>
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 focus:outline-none focus:border-primary font-nunito text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <FiX className="text-gray-400 hover:text-gray-600" size={18} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:border-primary font-nunito text-sm"
            >
              <option value="">Tous les statuts</option>
              <option value="NORMAL">Normal</option>
              <option value="ALERTE">Alerte</option>
              <option value="CRITIQUE">Critique</option>
            </select>

            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="border border-gray-300 px-3 py-2 focus:outline-none focus:border-primary font-nunito text-sm"
            >
              <option value="">Tous les stocks</option>
              <option value="normal">En stock</option>
              <option value="low">Stock faible</option>
              <option value="out">Rupture</option>
            </select>

            {(searchTerm || statusFilter || stockFilter) && (
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 font-nunito text-sm"
              >
                <FiRefreshCw size={16} />
                Réinitialiser
              </button>
            )}
          </div>
        </div>

        {/* Indicateurs de filtres actifs */}
        {(searchTerm || statusFilter || stockFilter) && (
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="font-nunito text-sm text-gray-600">
              Filtres actifs:
            </span>
            {searchTerm && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs font-medium">
                Recherche: "{searchTerm}"
              </span>
            )}
            {statusFilter && (
              <span className="bg-purple-100 text-purple-800 px-2 py-1 text-xs font-medium">
                Statut: {statusFilter}
              </span>
            )}
            {stockFilter && (
              <span className="bg-orange-100 text-orange-800 px-2 py-1 text-xs font-medium">
                Stock:{" "}
                {stockFilter === "normal"
                  ? "En stock"
                  : stockFilter === "low"
                    ? "Faible"
                    : "Rupture"}
              </span>
            )}
            <span className="bg-gray-100 text-gray-800 px-2 py-1 text-xs font-medium">
              {filteredReport.length} résultat
              {filteredReport.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Informations du rapport */}
      <div className="bg-white p-4 border border-gray-200 mb-4">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <h2 className="font-poppins font-semibold text-lg text-gray-900">
              Rapport d'inventaire
            </h2>
            <p className="font-nunito text-sm text-gray-600">
              {filteredReport.length} produit(s) correspondant aux critères
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-nunito text-sm text-gray-500">
              Dernière mise à jour: {new Date().toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>

      {/* Pagination en haut */}
      {totalPages > 1 && filteredReport.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
          <div className="font-nunito text-sm text-gray-600">
            Page {currentPage} sur {totalPages} • {filteredReport.length}{" "}
            produit{filteredReport.length > 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-xs font-nunito ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Tableau Desktop */}
      <div className="hidden lg:block bg-white border border-gray-200 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {reportColumns.map((column, index) => (
                  <th
                    key={index}
                    className="px-6 py-3 text-left font-poppins font-semibold text-xs text-gray-700 border-b border-gray-200"
                    style={{ width: column.width }}
                  >
                    {column.headerName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedReport.map((product, index) => (
                <tr key={product.id || index} className="hover:bg-gray-50">
                  {reportColumns.map((column, colIndex) => (
                    <td key={colIndex} className="px-6 py-4">
                      <div className="flex items-center h-full">
                        {column.render(product)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cartes Mobile/Tablette */}
      <div className="lg:hidden space-y-4 mb-6">
        {paginatedReport.map((product, index) => (
          <InventoryCard key={product.id || index} product={product} />
        ))}
      </div>

      {/* Pagination en bas */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 border border-gray-200">
          <div className="font-nunito text-sm text-gray-600">
            Page {currentPage} sur {totalPages} • {filteredReport.length}{" "}
            produit{filteredReport.length > 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronLeft size={18} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-8 h-8 text-xs font-nunito ${
                    currentPage === pageNum
                      ? "bg-primary text-white"
                      : "border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <BiChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      {/* Message vide */}
      {filteredReport.length === 0 && (
        <div className="text-center py-12 bg-white border border-gray-200">
          <BiPackage className="mx-auto text-gray-300" size={48} />
          <p className="font-nunito text-sm text-gray-600 mt-3">
            {searchTerm || statusFilter || stockFilter
              ? "Aucun produit ne correspond à vos critères"
              : "Aucun produit en inventaire"}
          </p>
          {searchTerm || statusFilter || stockFilter ? (
            <button
              onClick={resetFilters}
              className="mt-4 px-4 py-2 bg-primary text-white font-nunito text-sm hover:bg-primary/90"
            >
              Réinitialiser les filtres
            </button>
          ) : null}
        </div>
      )}

      {/* Modal Historique */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white w-full max-w-4xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary text-white">
              <h2 className="font-poppins font-semibold text-lg">
                Historique des mouvements
              </h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-1 hover:bg-white/20 transition-colors"
              >
                <BiX size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <div className="p-4">
                {modalLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : historyReport.length === 0 ? (
                  <p className="font-nunito text-sm text-gray-600 text-center py-8">
                    Aucun mouvement enregistré
                  </p>
                ) : (
                  <div className="space-y-3">
                    {historyReport.map((movement, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 border border-gray-200 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          {getMovementTypeIcon(movement.type)}
                          <div>
                            <p className="font-poppins font-semibold text-sm text-gray-900">
                              {getMovementTypeLabel(movement.type)}
                            </p>
                            <p className="font-nunito text-xs text-gray-600">
                              {movement.motif}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p
                            className={`font-poppins font-bold text-base ${
                              movement.type === "ENTREE"
                                ? "text-green-600"
                                : "text-red-600"
                            }`}
                          >
                            {movement.type === "ENTREE" ? "+" : "-"}
                            {movement.quantity}
                          </p>
                          <p className="font-nunito text-xs text-gray-500">
                            {new Date(movement.created_at).toLocaleDateString(
                              "fr-FR",
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-primary text-white font-nunito text-sm font-semibold hover:bg-primary/90"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// // Pages/InventairesPage.jsx
// import React, { useEffect, useState, useMemo } from "react";
// import { toast } from "react-toastify";
// import { useProductStore } from "./../../store/productStore";
// import {
//   BiPlus,
//   BiEdit,
//   BiTrash,
//   BiShow,
//   BiHistory,
//   BiTrendingUp,
//   BiTrendingDown,
//   BiAdjust,
//   BiX,
//   BiCopy,
//   BiPackage,
//   BiShoppingBag,
//   BiCheckCircle,
//   BiXCircle,
//   BiCalendar,
//   BiDownload,
//   BiSearch,
//   BiFilter,
// } from "react-icons/bi";
// import DataTable from "../../common/DataTable";
// import { FiAlertTriangle } from "react-icons/fi";

// export default function InventairesPage() {
//   const {
//     products,
//     loading,
//     error,
//     fetchProducts,
//     createMovement,
//     fetchInventoryReport,
//     inventoryReport,
//     historyReport,
//     exportInventoryPDF,
//     fetchProductHistory,
//   } = useProductStore();

//   const [modalLoading, setModalLoading] = useState(false);
//   const [showHistoryModal, setShowHistoryModal] = useState(false);

//   // États pour la recherche et filtres
//   const [searchTerm, setSearchTerm] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");
//   const [stockFilter, setStockFilter] = useState("");

//   // Chargement initial
//   useEffect(() => {
//     loadProducts();
//     loadInventoryReport();
//   }, []);

//   const loadProducts = async () => {
//     try {
//       await fetchProducts();
//     } catch (error) {
//       console.error("Erreur lors du chargement des produits:", error);
//       toast.error("Erreur lors du chargement des produits");
//     }
//   };

//   const loadInventoryReport = async () => {
//     try {
//       await fetchInventoryReport();
//       // toast.success("Rapport d'inventaire généré avec succès");
//     } catch (error) {
//       console.error("Erreur lors du chargement du rapport:", error);
//       toast.error("Erreur lors du chargement du rapport");
//     }
//   };

//   // Fonction pour exporter le PDF
//   const handleExportPDF = async () => {
//     try {
//       await exportInventoryPDF();
//       // toast.success("Rapport exporté en PDF avec succès");
//     } catch (error) {
//       toast.error("Erreur lors de l'export du PDF");
//     }
//   };

//   // Ouvrir modal d'historique
//   const handleShowHistory = async (product) => {
//     setModalLoading(true);
//     try {
//       await fetchProductHistory(product.id);
//       setShowHistoryModal(true);
//     } catch (error) {
//       toast.error("Erreur lors du chargement de l'historique");
//     } finally {
//       setModalLoading(false);
//     }
//   };

//   // Obtenir le statut du stock
//   const getStockStatus = (product) => {
//     const quantity = product.quantity || product.stockActuel || 0;
//     const seuilAlerte = product.seuilAlerte || 0;

//     if (quantity === 0) return "out-of-stock";
//     if (quantity <= seuilAlerte) return "low-stock";
//     return "in-stock";
//   };

//   const getMovementTypeIcon = (type) => {
//     switch (type) {
//       case "ENTREE":
//         return <BiTrendingUp size={14} />;
//       case "SORTIE":
//         return <BiTrendingDown size={14} />;
//       case "AJUSTEMENT":
//         return <BiAdjust size={14} />;
//       case "PERDU":
//         return <BiXCircle size={14} />;
//       default:
//         return <BiPackage size={14} />;
//     }
//   };

//   const getMovementTypeLabel = (type) => {
//     switch (type) {
//       case "ENTREE":
//         return "Entrée";
//       case "SORTIE":
//         return "Sortie";
//       case "AJUSTEMENT":
//         return "Ajustement";
//       case "PERDU":
//         return "Perte";
//       default:
//         return type;
//     }
//   };

//   const getStockStatusColor = (product) => {
//     const status = getStockStatus(product);
//     switch (status) {
//       case "out-of-stock":
//         return "bg-red-100 text-red-800 border border-red-200";
//       case "low-stock":
//         return "bg-yellow-100 text-yellow-800 border border-yellow-200";
//       default:
//         return "bg-green-100 text-green-800 border border-green-200";
//     }
//   };

//   const getStockStatusLabel = (product) => {
//     const status = getStockStatus(product);
//     switch (status) {
//       case "out-of-stock":
//         return "Rupture";
//       case "low-stock":
//         return "Stock faible";
//       default:
//         return "En stock";
//     }
//   };

//   // Fonction pour réinitialiser tous les filtres
//   const resetFilters = () => {
//     setSearchTerm("");
//     setStatusFilter("");
//     setStockFilter("");
//   };

//   // Filtrage des données avec useMemo pour optimiser les performances
//   const filteredReport = useMemo(() => {
//     return inventoryReport.filter((product) => {
//       // Filtre par recherche
//       const matchesSearch =
//         searchTerm === "" ||
//         product.libelle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//         product.code?.toLowerCase().includes(searchTerm.toLowerCase());

//       // Filtre par statut d'alerte
//       const matchesStatus =
//         statusFilter === "" || product.statutAlerte === statusFilter;

//       // Filtre par niveau de stock
//       const matchesStock =
//         stockFilter === "" ||
//         (stockFilter === "low" && getStockStatus(product) === "low-stock") ||
//         (stockFilter === "out" && getStockStatus(product) === "out-of-stock") ||
//         (stockFilter === "normal" && getStockStatus(product) === "in-stock");

//       return matchesSearch && matchesStatus && matchesStock;
//     });
//   }, [inventoryReport, searchTerm, statusFilter, stockFilter]);

//   // Statistiques filtrées
//   const filteredStats = useMemo(() => {
//     const total = filteredReport.length;
//     const lowStock = filteredReport.filter(
//       (p) => getStockStatus(p) === "low-stock",
//     ).length;
//     const outOfStock = filteredReport.filter(
//       (p) => getStockStatus(p) === "out-of-stock",
//     ).length;
//     const totalStock = filteredReport.reduce((sum, product) => {
//       const quantity = product.quantity || product.stockActuel || 0;
//       return sum + quantity;
//     }, 0);

//     return { total, lowStock, outOfStock, totalStock };
//   }, [filteredReport]);

//   // Colonnes pour le rapport d'inventaire
//   const reportColumns = [
//     {
//       field: "libelle",
//       headerName: "Produit",
//       width: 200,
//       renderCell: ({ row }) => (
//         <div className="flex items-center gap-2">
//           <BiPackage className="text-secondary-600" size={16} />
//           <div>
//             <span className="font-poppins font-medium text-[15px] text-subtitle block">
//               {row.libelle}
//             </span>
//             {row.code && (
//               <span className="font-nunito text-[12px] text-gray-500">
//                 {row.code}
//               </span>
//             )}
//           </div>
//         </div>
//       ),
//     },
//     {
//       field: "quantity_initiale",
//       headerName: "Stock initial",
//       width: 120,
//       renderCell: ({ row }) => (
//         <span className="font-poppins font-medium text-[15px] text-subtitle">
//           {row.quantity_initiale || 0}
//         </span>
//       ),
//     },
//     {
//       field: "stockActuel",
//       headerName: "Stock actuel",
//       width: 120,
//       renderCell: ({ row }) => (
//         <span className="font-poppins font-medium text-[15px] text-subtitle">
//           {row.stockActuel || 0}
//         </span>
//       ),
//     },
//     {
//       field: "entrees",
//       headerName: "Entrées",
//       width: 100,
//       renderCell: ({ row }) => (
//         <div className="flex items-center gap-1 text-green-600">
//           <BiTrendingUp size={14} />
//           <span className="font-poppins font-bold text-[16px]">
//             {row.entrees || 0}
//           </span>
//         </div>
//       ),
//     },
//     {
//       field: "sorties",
//       headerName: "Sorties",
//       width: 100,
//       renderCell: ({ row }) => (
//         <div className="flex items-center gap-1 text-red-600">
//           <BiTrendingDown size={14} />
//           <span className="font-poppins font-bold text-[16px]">
//             {row.sorties || 0}
//           </span>
//         </div>
//       ),
//     },
//     {
//       field: "statutAlerte",
//       headerName: "Statut alerte",
//       width: 130,
//       renderCell: ({ row }) => (
//         <span
//           className={`px-2 py-1 rounded-full text-xs font-medium ${getStockStatusColor(
//             row,
//           )}`}
//         >
//           {getStockStatusLabel(row)}
//         </span>
//       ),
//     },
//     {
//       field: "stock_final",
//       headerName: "Stock final",
//       width: 120,
//       renderCell: ({ row }) => {
//         const stockFinal = row.stock_final || 0;
//         return (
//           <div className="text-center">
//             <span className="font-poppins font-bold text-[16px] text-gray-900">
//               {stockFinal}
//             </span>
//           </div>
//         );
//       },
//     },
//     {
//       field: "actions",
//       headerName: "Actions",
//       width: 200,
//       renderCell: ({ row }) => (
//         <div className="flex items-center gap-1">
//           <button
//             onClick={() => handleShowHistory(row)}
//             className="flex items-center gap-1 px-3 py-1 font-nunito font-semibold text-[13px] bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors rounded"
//             title="Historique"
//           >
//             <BiHistory size={20} />
//           </button>
//         </div>
//       ),
//     },
//   ];

//   return (
//     <div className="md:p-6">
//       {/* En-tête */}
//       <div className="mb-6">
//         <h1 className="font-poppins font-semibold text-[18px] text-title">
//           Gestion des Inventaires
//         </h1>
//         <p className="font-nunito text-[14px] text-text mt-2">
//           Suivez et gérez les mouvements de stock en temps réel
//         </p>
//       </div>

//       {/* Barre d'actions et recherche */}
//       <div className="bg-white p-4 shadow-sm border border-gray-200 rounded-lg mb-6">
//         <div className="flex flex-wrap gap-3 w-full">
//           {/* Barre de recherche */}
//           <div className="flex-1 min-w-[250px]">
//             <div className="relative">
//               <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
//                 <BiSearch size={18} className="text-gray-400" />
//               </div>
//               <input
//                 type="text"
//                 placeholder="Rechercher un produit..."
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className="w-full pl-10 pr-4 py-2 border border-gray-300 font-nunito text-[14px] text-text rounded focus:outline-none focus:ring-2 focus:ring-secondary-500"
//               />
//             </div>
//           </div>

//           {/* Filtre par statut d'alerte */}
//           <div className="flex-1 min-w-[180px]">
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text rounded focus:outline-none focus:ring-2 focus:ring-secondary-500"
//             >
//               <option value="">Tous les statuts d'alerte</option>
//               <option value="NORMAL">Normal</option>
//               <option value="ALERTE">Alerte</option>
//               <option value="CRITIQUE">Critique</option>
//             </select>
//           </div>

//           {/* Filtre par niveau de stock */}
//           <div className="flex-1 min-w-[180px]">
//             <select
//               value={stockFilter}
//               onChange={(e) => setStockFilter(e.target.value)}
//               className="w-full border border-gray-300 px-3 py-2 font-nunito text-[14px] text-text rounded focus:outline-none focus:ring-2 focus:ring-secondary-500"
//             >
//               <option value="">Tous les niveaux</option>
//               <option value="normal">En stock</option>
//               <option value="low">Stock faible</option>
//               <option value="out">Rupture</option>
//             </select>
//           </div>

//           {/* Boutons d'action */}
//           <div className="flex flex-wrap gap-2 flex-1 md:min-w-[320px]">
//             <button
//               onClick={loadInventoryReport}
//               disabled={loading}
//               className="flex items-center justify-center gap-2 px-4 py-2 bg-secondary-600 text-white font-nunito font-semibold text-[14px] hover:bg-secondary-700 transition-colors rounded flex-1 min-w-[150px] disabled:opacity-50"
//             >
//               <BiCalendar size={16} />
//               Actualiser
//             </button>
//             <button
//               onClick={handleExportPDF}
//               disabled={loading}
//               className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white font-nunito font-semibold text-[14px] hover:bg-green-700 transition-colors rounded flex-1 min-w-[140px] disabled:opacity-50"
//             >
//               <BiDownload size={16} />
//               Exporter PDF
//             </button>
//             {(searchTerm || statusFilter || stockFilter) && (
//               <button
//                 onClick={resetFilters}
//                 className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-500 text-white font-nunito font-semibold text-[14px] hover:bg-gray-600 transition-colors rounded"
//               >
//                 <BiX size={16} />
//                 Réinitialiser
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Indicateurs de filtres actifs */}
//         {(searchTerm || statusFilter || stockFilter) && (
//           <div className="mt-3 flex flex-wrap gap-2">
//             {searchTerm && (
//               <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
//                 Recherche: "{searchTerm}"
//                 <button
//                   onClick={() => setSearchTerm("")}
//                   className="hover:text-blue-900"
//                 >
//                   <BiX size={12} />
//                 </button>
//               </span>
//             )}
//             {statusFilter && (
//               <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
//                 Statut: {statusFilter}
//                 <button
//                   onClick={() => setStatusFilter("")}
//                   className="hover:text-purple-900"
//                 >
//                   <BiX size={12} />
//                 </button>
//               </span>
//             )}
//             {stockFilter && (
//               <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
//                 Stock:{" "}
//                 {stockFilter === "normal"
//                   ? "En stock"
//                   : stockFilter === "low"
//                     ? "Faible"
//                     : "Rupture"}
//                 <button
//                   onClick={() => setStockFilter("")}
//                   className="hover:text-orange-900"
//                 >
//                   <BiX size={12} />
//                 </button>
//               </span>
//             )}
//           </div>
//         )}
//       </div>

//       {/* Statistiques */}
//       <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//         <div className="bg-white p-4 shadow-sm border border-gray-200 rounded-lg">
//           <div className="flex items-center gap-2">
//             <BiPackage className="text-blue-600" size={20} />
//             <p className="font-nunito text-[14px] text-text">
//               Produits filtrés
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {filteredStats.total}
//           </p>
//           <p className="font-nunito text-[12px] text-gray-500">
//             sur {inventoryReport.length} total
//           </p>
//         </div>
//         <div className="bg-white p-4 shadow-sm border border-gray-200 rounded-lg">
//           <div className="flex items-center gap-2">
//             <BiShoppingBag className="text-purple-600" size={20} />
//             <p className="font-nunito text-[14px] text-text">
//               Stock total filtré
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {filteredStats.totalStock}
//           </p>
//         </div>
//         <div className="bg-white p-4 shadow-sm border border-gray-200 rounded-lg">
//           <div className="flex items-center gap-2">
//             <FiAlertTriangle className="text-yellow-600" size={20} />
//             <p className="font-nunito text-[14px] text-text">Stock faible</p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {filteredStats.lowStock}
//           </p>
//         </div>
//         <div className="bg-white p-4 shadow-sm border border-gray-200 rounded-lg">
//           <div className="flex items-center gap-2">
//             <BiXCircle className="text-red-600" size={20} />
//             <p className="font-nunito text-[14px] text-text">
//               Rupture de stock
//             </p>
//           </div>
//           <p className="font-poppins font-bold text-[16px] text-montant mt-2">
//             {filteredStats.outOfStock}
//           </p>
//         </div>
//       </div>

//       {/* Rapport d'inventaire */}
//       <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
//         <div className="p-4 border-b border-gray-200">
//           <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
//             <div>
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Rapport d'inventaire
//               </h2>
//               <p className="font-nunito text-[14px] text-text">
//                 {filteredReport.length} produit(s) correspondant aux critères
//               </p>
//             </div>
//             <div className="flex items-center gap-2">
//               <span className="font-nunito text-[14px] text-text">
//                 Dernière mise à jour: {new Date().toLocaleDateString()}
//               </span>
//             </div>
//           </div>
//         </div>
//         <DataTable
//           rows={filteredReport}
//           columns={reportColumns}
//           loading={loading}
//           showToolbar={false}
//           autoHeight={true}
//           className="font-nunito text-[14px]"
//         />
//       </div>

//       {/* Modal Historique */}
//       {showHistoryModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
//           <div className="bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg">
//             <div className="flex items-center justify-between p-4 border-b border-gray-200">
//               <h2 className="font-poppins font-semibold text-[18px] text-title">
//                 Historique des mouvements
//               </h2>
//               <button
//                 onClick={() => setShowHistoryModal(false)}
//                 className="p-1 hover:bg-gray-100 transition-colors rounded"
//               >
//                 <BiX size={20} />
//               </button>
//             </div>

//             <div className="p-4">
//               {modalLoading ? (
//                 <div className="flex justify-center py-8">
//                   <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-600"></div>
//                 </div>
//               ) : historyReport.length === 0 ? (
//                 <p className="font-nunito text-[14px] text-text text-center py-8">
//                   Aucun mouvement enregistré
//                 </p>
//               ) : (
//                 <div className="space-y-3">
//                   {historyReport.map((movement, index) => (
//                     <div
//                       key={index}
//                       className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
//                     >
//                       <div className="flex items-center gap-3">
//                         {getMovementTypeIcon(movement.type)}
//                         <div>
//                           <p className="font-poppins font-medium text-[15px] text-subtitle">
//                             {getMovementTypeLabel(movement.type)}
//                           </p>
//                           <p className="font-nunito text-[14px] text-text">
//                             {movement.motif}
//                           </p>
//                         </div>
//                       </div>
//                       <div className="text-right">
//                         <p
//                           className={`font-poppins font-bold text-[16px] ${
//                             movement.type === "ENTREE"
//                               ? "text-green-600"
//                               : "text-red-600"
//                           }`}
//                         >
//                           {movement.type === "ENTREE" ? "+" : "-"}
//                           {movement.quantity}
//                         </p>
//                         <p className="font-nunito text-[14px] text-text">
//                           {new Date(movement.created_at).toLocaleDateString(
//                             "fr-FR",
//                           )}
//                         </p>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             <div className="p-4 border-t border-gray-200 flex justify-end">
//               <button
//                 onClick={() => setShowHistoryModal(false)}
//                 className="px-4 py-2 bg-secondary-600 text-white font-nunito font-semibold text-[14px] hover:bg-secondary-700 transition-colors rounded"
//               >
//                 Fermer
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
